import { motion } from "framer-motion";
import { useTranslations } from "next-intl";
import { HorizontalContainer } from "@/components/layout/containers/HorizontalContainer";
import { VerticalContainer } from "@/components/layout/containers/VerticalContainer";
import { SpendAnalysisIcon } from "@/components/icons/SpendAnalysisIcon";
import { SearchContext } from "./filterSuggestions";
import { MapIcon } from "@/components/icons/MapIcon";

interface SearchContextSwitchProps {
  searchContext: SearchContext;
  onContextChange: (context: SearchContext) => void;
}

/**
 * Toggle switch at the bottom of the search dropdown.
 * Switches between "spend" and "map" search contexts.
 * Does NOT navigate — only changes the search context for the dropdown.
 */
export function SearchContextSwitch({ searchContext, onContextChange }: SearchContextSwitchProps) {
  const t = useTranslations();

  const handleSwitch = () => {
    onContextChange(searchContext === "spend" ? "map" : "spend");
  };

  return (
    <div className={`relative flex items-center justify-between overflow-hidden border-t border-border p-5 transition-colors duration-500 ease-in-out ${searchContext === "spend" ? "bg-blue-200/20" : "bg-emerald-50/70"}`}>
      <VerticalContainer className="z-10 gap-1.5">
        <HorizontalContainer>
          {searchContext === "spend" ? <SpendAnalysisIcon className="h-4 w-4 text-slate-500" /> : <MapIcon className="h-4 w-4 text-emerald-600" />}
          <span className={`text-[11px] font-bold uppercase tracking-widest ${searchContext === "spend" ? "text-slate-500" : "text-emerald-700"}`}>{searchContext === "spend" ? t("search.spendAnalysis") : t("search.mapSearch")}</span>
        </HorizontalContainer>
        <span className="text-sm text-muted-foreground">{searchContext === "spend" ? t("search.searchAcross") : t("search.exploreGeographicalData")}</span>
      </VerticalContainer>

      <button
        type="button"
        onClick={handleSwitch}
        className="relative z-10 ml-6 flex h-9 w-[72px] shrink-0 cursor-pointer items-center rounded-full border border-slate-200/60 bg-white p-1 shadow-sm transition-colors hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
      >
        <div className={`absolute inset-0 rounded-full transition-opacity duration-500 ${searchContext === "spend" ? "bg-slate-50/50" : "bg-emerald-50/50"}`} />

        <motion.div
          layout
          initial={false}
          className={`absolute box-border flex h-7 w-7 items-center justify-center rounded-full shadow-[0_2px_8px_rgba(0,0,0,0.12)] transition-colors duration-300 ${searchContext === "spend" ? "bg-slate-800" : "bg-emerald-600"}`}
          animate={{
            x: searchContext === "spend" ? 0 : 36,
          }}
          transition={{ type: "spring", stiffness: 500, damping: 30 }}
        >
          {searchContext === "spend" ? <SpendAnalysisIcon className="h-3.5 w-3.5 text-white" /> : <MapIcon className="h-3.5 w-3.5 text-white" />}
        </motion.div>

        <div className="pointer-events-none relative z-10 flex w-full items-center justify-between px-[4px]">
          <SpendAnalysisIcon className={`h-4 w-4 transition-all duration-300 ${searchContext === "spend" ? "scale-50 opacity-0" : "scale-100 text-slate-400 opacity-100"}`} />
          <MapIcon className={`h-4 w-4 transition-all duration-300 ${searchContext === "map" ? "scale-50 opacity-0" : "scale-100 text-emerald-500 opacity-100"}`} />
        </div>
      </button>
    </div>
  );
}
